/* پرداخت تنخواه (تنخواه دستگاه‌ها) */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── کمکی‌ها ── */
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const API    = (path,opts={}) => fetch(`/api/deposit${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(jsonOf, netErr);

/* نوع تنخواه — ایندکس همان مقدار FundKind در دیتابیس */
const FUND_KINDS = ['موقت','دائم','تسویه تنخواه موقت','کاهش تنخواه دائم'];

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

const FSelect = ({label,name,value,onChange,options,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value??''} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',
        width:'100%',boxSizing:'border-box',background:'#fff'}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const FDate = ({label,value,onChange,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <JalaliDatePicker value={value} onChange={onChange} placeholder={label}/>
  </div>
);

/* ستون‌های گرید اصلی */
const INIT_WIDTHS = [45,260,120,110,120,110,130,140,140,140,140,120,110];

const EMPTY = {department_id:0,department:'',request_no:'',request_date:'',
  pay_no:'',pay_date:'',fund_pay:'',fund_kind:0,cheque_no:'',cheque_date:''};

export default function DepositFundList({ user, onBack }) {
  // دکمه‌های فرم FRMDEPARTMENTFUND — دیدن دکمه = داشتن مجوز
  const vbf = useFormButtons('FRMDEPARTMENTFUND', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  /* ── گرید اصلی ── */
  const [rows,setRows]         = useState([]);
  const [total,setTotal]       = useState(0);
  const [hasMore,setHasMore]   = useState(false);
  const [loading,setLoading]   = useState(false);
  const [selected,setSelected] = useState(null);

  /* ── جمع‌ها (روی کل نتیجه فیلتر، نه فقط صفحه جاری) ── */
  const [sums,setSums] = useState({sum_permanent:0,sum_temporary:0,sum_fund:0});

  /* ── فیلترها ── */
  const [dept,setDept]   = useState(0);
  const [kind,setKind]   = useState('');
  const [depts,setDepts] = useState([]);
  const [ready,setReady] = useState(false);

  /* ── مودال‌ها ── */
  const [modal,setModal]   = useState(null);   // new | edit | delete | balance
  const [form,setForm]     = useState(EMPTY);
  const [error,setError]   = useState('');
  const [toast,setToast]   = useState('');
  const [lookup,setLookup] = useState([]);
  const [balance,setBalance] = useState([]);

  const offsetRef = useRef(0);

  const {TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['department','cheque_no','request_no','pay_no'],
    serverTotal:total, serverHasMore:hasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* مقداردهی اولیه */
  useEffect(()=>{ (async()=>{
    try{
      const d = await API('/funds/init/');
      if(d.ok) setDepts(d.departments||[]);
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await API(`/funds/?offset=${off}&department_id=${dept}&kind=${kind}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setHasMore(d.has_more);
        setSums({sum_permanent:d.sum_permanent,sum_temporary:d.sum_temporary,sum_fund:d.sum_fund});
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[dept,kind]);

  useEffect(()=>{ if(ready) fetchData(true); },[ready,dept,kind]); // eslint-disable-line

  const handleChange = e => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  /* ── جدید / ویرایش ── */
  const openNew = ()=>{ if (!vbf.can('BTNNEW')) return;
    setForm({...EMPTY, department_id:dept||0,
      department:depts.find(d=>d.id===dept)?.title||''});
    setError(''); setLookup([]); setModal('new');
  };
  const openEdit = ()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected) return;
    setForm({department_id:selected.department_id, department:selected.department,
      request_no:selected.request_no, request_date:selected.request_date,
      pay_no:selected.pay_no, pay_date:selected.pay_date,
      fund_pay:selected.fund_pay, fund_kind:selected.fund_kind,
      cheque_no:selected.cheque_no, cheque_date:selected.cheque_date});
    setError(''); setLookup([]); setModal('edit');
  };

  /* اعتبارسنجی — هر دو شرط یک پیام مشترک دارند */
  const validate = f => {
    if(Number(f.department_id||0)===0 || Number(f.fund_pay||0)===0) return 'اطلاعات کامل نیست';
    return null;
  };

  const handleSave = async()=>{
    const v = validate(form);
    if(v){ setError(v); return; }
    setError('');
    const url = modal==='new' ? '/funds/create/' : `/funds/${selected.id}/update/`;
    const d = await API(url,{method:'POST',body:JSON.stringify(form)});
    if(d.ok){
      setModal(null); fetchData(true);
      showToast(modal==='new'?'✅ ثبت شد':'✅ ویرایش شد');
    } else setError(d.error||'خطا');
  };

  const openDelete = ()=>{ if (!vbf.can('BTNDELETE')) return; if(!selected) return; setError(''); setModal('delete'); };
  const handleDelete = async()=>{
    const d = await API(`/funds/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){ setModal(null); setSelected(null); fetchData(true); showToast('✅ حذف شد'); }
    else setError(d.error||'خطا');
  };

  const openBalance = async()=>{
    const d = await API('/funds/balance/');
    if(d.ok){ setBalance(d.rows); setModal('balance'); }
    else alert(d.error||'خطا');
  };

  const doLookup = async q => {
    const d = await API(`/departments/lookup/?q=${encodeURIComponent(q||'')}`);
    if(d.ok) setLookup(d.rows);
  };

  const deptOpts = [{value:0,label:'همه واحدها'},
    ...depts.map(d=>({value:d.id,label:d.title}))];
  const kindOpts = [{value:'',label:'همه انواع'},
    ...FUND_KINDS.map((t,n)=>({value:n,label:t}))];

  /* رنگ ردیف بر اساس نوع تنخواه — دائم سبز، موقت آبی، کاهش/تسویه قرمز کمرنگ */
  const kindBg = k => k===1?'#e8f5e9' : k===0?'#e3f2fd' : '#ffebee';

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>پرداخت تنخواه</div><div style={s.hSub}>تنخواه دستگاهها — بخش سپرده</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        {/* ── نوار ابزار ── */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={openDelete} disabled={!selected} style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            <button onClick={openBalance} style={s.btnBalance}>📊 مانده تنخواه واحدها</button>
          </div>
        </div>

        {/* ── فیلترها + جستجو ── */}
        <div style={s.toolbar2}>
          <div style={{width:'280px'}}>
            <FSelect label="واحد اجرایی" name="dept" value={dept}
              onChange={e=>setDept(Number(e.target.value))} options={deptOpts}/>
          </div>
          <div style={{width:'200px'}}>
            <FSelect label="نوع تنخواه" name="kind" value={kind}
              onChange={e=>setKind(e.target.value)} options={kindOpts}/>
          </div>
          <div style={{marginRight:'auto'}}>
            <div style={s.fL}>جستجو</div>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="واحد اجرایی / شماره چک" style={{...s.fI,width:'280px'}}/>
          </div>
        </div>

        {/* ── گرید اصلی ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1} right>واحد اجرایی</TH>
                <TH idx={2}>شماره درخواست</TH>
                <TH idx={3}>تاریخ درخواست</TH>
                <TH idx={4}>شماره پرداخت</TH>
                <TH idx={5}>تاریخ پرداخت</TH>
                <TH idx={6} gold>مبلغ</TH>
                <TH idx={7} green>تنخواه دائم</TH>
                <TH idx={8} red>کاهش تنخواه دائم</TH>
                <TH idx={9} green>تنخواه موقت</TH>
                <TH idx={10} red>تسویه تنخواه موقت</TH>
                <TH idx={11}>شماره چک</TH>
                <TH idx={12}>تاریخ چک</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={13} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6' : kindBg(r.fund_kind);
                return (
                  <tr key={r.id} onClick={()=>setSelected(r)} style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.department}>{r.department}</td>
                    <td style={s.td}>{toFa(r.request_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.request_date)}</td>
                    <td style={s.td}>{toFa(r.pay_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.pay_date)}</td>
                    <td style={{...s.td,...s.num}}>{numFmt(r.fund_pay)}</td>
                    <td style={{...s.td,...s.num,color:'#1b5e20'}}>{numFmt(r.permanent_fund)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.permanent_decrease)}</td>
                    <td style={{...s.td,...s.num,color:'#1565c0'}}>{numFmt(r.temporary_fund)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.temporary_settle)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.cheque_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.cheque_date)}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        {/* ── نوار جمع‌ها ── */}
        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          <span>جمع تنخواه دائم: <b style={{color:'#1b5e20'}}>{numFmt(sums.sum_permanent)||'۰'}</b> ریال</span>
          <span>جمع تنخواه موقت: <b style={{color:'#1565c0'}}>{numFmt(sums.sum_temporary)||'۰'}</b> ریال</span>
          <span>جمع کل تنخواه: <b style={{color:'#6a1b9a'}}>{numFmt(sums.sum_fund)||'۰'}</b> ریال</span>
          {selected && <span>ردیف انتخابی: <b>{selected.kind_title}</b> — <b>{numFmt(selected.fund_pay)}</b></span>}
        </div>
      </main>

      {/* ══ مودال جدید / ویرایش ══ */}
      {(modal==='new'||modal==='edit') && (
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{modal==='new'?'➕ ثبت تنخواه جدید':'✎ ویرایش تنخواه'}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <div style={s.row}>
                <div style={{flex:'0 0 60%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>واحد اجرایی *</label>
                  <input value={form.department} placeholder="تایپ کنید..."
                    onChange={e=>{setForm(p=>({...p,department:e.target.value,department_id:0}));doLookup(e.target.value);}}
                    onFocus={()=>doLookup(form.department)} style={s.fI}/>
                  {lookup.length>0&&(
                    <div style={s.dropdown}>{lookup.map(r=>(
                      <div key={r.id} onClick={()=>{setForm(p=>({...p,department_id:r.id,department:r.title}));setLookup([]);}}
                        style={s.dropItem}>{toFa(r.code)} — {r.title}</div>))}</div>)}
                </div>
                <FSelect label="نوع تنخواه" name="fund_kind" value={form.fund_kind}
                  onChange={e=>setForm(p=>({...p,fund_kind:Number(e.target.value)}))}
                  options={FUND_KINDS.map((t,n)=>({value:n,label:t}))} flex="0 0 38%"/>
              </div>
              <div style={s.row}>
                <FInput label="نامه درخواست: شماره" name="request_no" value={form.request_no}
                  onChange={handleChange} flex="0 0 32%"/>
                <FDate label="تاریخ درخواست" value={form.request_date}
                  onChange={v=>setForm(p=>({...p,request_date:v}))} flex="0 0 32%"/>
                <FInput label="مبلغ تنخواه *" name="fund_pay" value={form.fund_pay}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 32%"/>
              </div>
              <div style={s.row}>
                <FInput label="نامه پرداخت: شماره" name="pay_no" value={form.pay_no}
                  onChange={handleChange} flex="0 0 32%"/>
                <FDate label="تاریخ پرداخت" value={form.pay_date}
                  onChange={v=>setForm(p=>({...p,pay_date:v}))} flex="0 0 32%"/>
              </div>
              <div style={s.row}>
                <FInput label="شماره چک" name="cheque_no" value={form.cheque_no}
                  onChange={handleChange} dir="ltr" flex="0 0 32%"/>
                <FDate label="تاریخ چک" value={form.cheque_date}
                  onChange={v=>setForm(p=>({...p,cheque_date:v}))} flex="0 0 32%"/>
              </div>
              <div style={s.infoBox}>
                نوع تنخواه در محاسبه مانده اثر معکوس دارد:
                «کاهش تنخواه دائم» از جمع دائم و «تسویه تنخواه موقت» از جمع موقت کسر می‌شود.
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال حذف ══ */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'480px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>
                آیا از حذف تنخواه <b>{selected.kind_title}</b> واحد <b>{selected.department}</b> به
                مبلغ <b>{numFmt(selected.fund_pay)}</b> ریال مطمئن هستید؟</p>
              <p style={{fontSize:'12px',color:'#e65100'}}>
                ⚠️ با حذف این ردیف، مانده تنخواه این واحد تغییر می‌کند.</p>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNDELETE') && <button onClick={handleDelete}
                style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال مانده تنخواه واحدها ══ */}
      {modal==='balance'&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'1000px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#37474f,#455a64)'}}>
              <div style={s.mTitle}>📊 مانده تنخواه به تفکیک واحد اجرایی</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              <div style={{overflow:'auto',maxHeight:'60vh'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>
                    {['واحد اجرایی','تعداد','تنخواه دائم','کاهش دائم','مانده دائم',
                      'تنخواه موقت','تسویه موقت','مانده موقت','مانده کل']
                      .map(h=><th key={h} style={s.subTh}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {balance.length===0&&<tr><td colSpan={9} style={s.empty}>اطلاعاتی نیست</td></tr>}
                    {balance.map((r,idx)=>(
                      <tr key={r.department_id} style={{background:idx%2===0?'#fff':'#eceff1'}}>
                        <td style={{...s.subTd,textAlign:'right'}}>{r.department}</td>
                        <td style={s.subTd}>{toFa(r.count)}</td>
                        <td style={{...s.subTd,...s.num,color:'#1b5e20'}}>{numFmt(r.permanent_fund)}</td>
                        <td style={{...s.subTd,...s.num,color:'#c62828'}}>{numFmt(r.permanent_decrease)}</td>
                        <td style={{...s.subTd,...s.num,fontWeight:'700'}}>{numFmt(r.permanent_remain)}</td>
                        <td style={{...s.subTd,...s.num,color:'#1565c0'}}>{numFmt(r.temporary_fund)}</td>
                        <td style={{...s.subTd,...s.num,color:'#c62828'}}>{numFmt(r.temporary_settle)}</td>
                        <td style={{...s.subTd,...s.num,fontWeight:'700'}}>{numFmt(r.temporary_remain)}</td>
                        <td style={{...s.subTd,...s.num,color:'#6a1b9a',fontWeight:'700'}}>{numFmt(r.total_remain)}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'flex-end',gap:'12px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px',flexWrap:'wrap'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  btnNew:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'7px 16px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnBalance:{padding:'7px 16px',background:'linear-gradient(135deg,#37474f,#455a64)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'20px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subTh:{padding:'7px 9px',background:'#0d2137',color:'#C8A84B',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'720px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center'},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  infoBox:{background:'#e3f2fd',border:'1px solid #90caf9',borderRadius:'8px',padding:'9px 14px',color:'#0d47a1',fontSize:'12px',lineHeight:'1.9'},
  dropdown:{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',maxHeight:'190px',overflowY:'auto',zIndex:50,boxShadow:'0 6px 20px rgba(0,0,0,0.18)'},
  dropItem:{padding:'7px 11px',fontSize:'12px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',textAlign:'right'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};
